/**
 * Tags personalizadas (docs/PLANO_TAGS.md): tags e categorias do usuário e do
 * escritório, aplicadas em processos, clientes, modelos de petição e documentos.
 */
import { api } from './client';

/** A tag como aparece num registro (chip). */
export interface TagResumo {
  id: string;
  nome: string;
  cor?: string | null;
  categoria_id?: string | null;
}

/**
 * Em quantos registros (que a pessoa vê) a tag está, por recurso. `null`: o recurso
 * não foi contado (modelos na conta de outra pessoa) — não é zero.
 */
export interface TagUso {
  processos: number | null;
  clientes: number | null;
  modelos: number | null;
  artefatos: number | null;
}

export interface Tag extends TagResumo {
  descricao?: string | null;
  /** Criador. Na tag pessoal é o dono. */
  usuario_id?: string | null;
  /** Nulo = pessoal; preenchido = do escritório. */
  escritorio_id?: string | null;
  /** Preenchido = da equipe (com o `escritorio_id` dela): quem está nela, o proprietário e os sócios a veem. */
  equipe_id?: string | null;
  /** Editar e excluir: o dono (pessoal) ou criador, proprietário e sócio (escritório). */
  pode_gerir: boolean;
  created_at: string;
  /** Só em `fetchTagsComUso` (tela de Tags). */
  uso?: TagUso;
}

export interface TagCategoria {
  id: string;
  nome: string;
  ordem: number;
  usuario_id?: string | null;
  escritorio_id?: string | null;
  pode_gerir: boolean;
}

/** Equipe cujas tags a pessoa vê: a seção "Da equipe" e o escopo da tag nova. */
export interface TagEquipe {
  id: string;
  nome: string;
  escritorio_id: string;
}

export interface TagsDisponiveis {
  items: Tag[];
  total: number;
  categorias: TagCategoria[];
  /** Ausente na resposta de um backend anterior às tags de equipe. */
  equipes?: TagEquipe[];
}

/** Recursos que recebem tag — o segmento da URL em `/tags/{recurso}/...`. */
export type RecursoTag = 'processos' | 'clientes' | 'modelos' | 'artefatos';

export async function fetchTags(): Promise<TagsDisponiveis> {
  const { data } = await api.get<TagsDisponiveis>('/tags');
  return data;
}

/** As tags com o uso de cada uma (tela de Tags). O popup e o filtro não pagam a contagem. */
export async function fetchTagsComUso(): Promise<TagsDisponiveis> {
  const { data } = await api.get<TagsDisponiveis>('/tags', { params: { com_uso: true } });
  return data;
}

export interface TagInput {
  nome: string;
  cor: string;
  categoria_id?: string | null;
  escritorio_id?: string | null;
  equipe_id?: string | null;
}

export async function createTag(payload: TagInput): Promise<Tag> {
  const { data } = await api.post<Tag>('/tags', payload);
  return data;
}

export async function updateTag(
  id: string,
  payload: { nome?: string; cor?: string; categoria_id?: string | null },
): Promise<Tag> {
  const { data } = await api.put<Tag>(`/tags/${id}`, payload);
  return data;
}

export async function deleteTag(id: string): Promise<void> {
  await api.delete(`/tags/${id}`);
}

export async function createCategoria(payload: { nome: string; escritorio_id?: string | null }): Promise<TagCategoria> {
  const { data } = await api.post<TagCategoria>('/tags/categorias', payload);
  return data;
}

export async function updateCategoria(id: string, payload: { nome?: string; ordem?: number }): Promise<TagCategoria> {
  const { data } = await api.put<TagCategoria>(`/tags/categorias/${id}`, payload);
  return data;
}

export async function deleteCategoria(id: string): Promise<void> {
  await api.delete(`/tags/categorias/${id}`);
}

/** URL do conjunto de tags de um registro (PUT idempotente — também a do outbox). */
export function urlTagsDoRegistro(recurso: RecursoTag, id: string): string {
  return `/tags/${recurso}/${id}`;
}

/** Deixa no registro exatamente estas tags (entre as que a pessoa vê). */
export async function setTagsDoRegistro(recurso: RecursoTag, id: string, tagIds: string[]): Promise<TagResumo[]> {
  const { data } = await api.put<{ tags: TagResumo[] }>(urlTagsDoRegistro(recurso, id), { tag_ids: tagIds });
  return data.tags;
}

export async function aplicarTagsEmLote(
  recurso: RecursoTag,
  payload: { ids: string[]; adicionar?: string[]; remover?: string[] },
): Promise<{ alterados: number }> {
  const { data } = await api.post<{ alterados: number }>(`/tags/${recurso}/lote`, payload);
  return data;
}
